import forge from 'node-forge'
import { base64BigInt, bigintBase64, findProps } from './util'
import { URL_WRAPPING_CRYPTO_KEY, URL_GENERATE_WRAPPING_KEY } from '../constants/urls'
import debug from '../core/debug'
import cache from './cache'

/**
 * PEM keys
 * export
 */

function exportPrivateKeyToPEM (privateKey) {
  return forge.pki.privateKeyToPem(privateKey)
}

function exportPublicKeyToPEM (publicKey) {
  return forge.pki.publicKeyToPem(publicKey)
}

export function exportPEM ({ privateKey, publicKey }) {
  return {
    publicPEM: exportPublicKeyToPEM(publicKey),
    privatePEM: exportPrivateKeyToPEM(privateKey)
  }
}

function importPrivateKeyFromPEM (privatePEM) {
  return forge.pki.privateKeyFromPem(privatePEM)
}

function importPublicKeyFromPEM (publicPEM) {
  return forge.pki.publicKeyFromPem(publicPEM)
}

export function importPEM ({ privatePEM, publicPEM }) {
  return {
    publicKey: importPublicKeyFromPEM(publicPEM),
    privateKey: importPrivateKeyFromPEM(privatePEM)
  }
}

/**
 * generate key
 */

export function generateKey () {
  return new Promise((resolve, reject) => {
    forge.pki.rsa.generateKeyPair(
      {
        bits: 2048,
        workers: 2,
        e: 0x10001
      },
      (err, { publicKey, privateKey }) => {
        if (err) {
          return reject(err)
        }
        return resolve({ publicKey, privateKey })
      }
    )
  })
}

/**
 * fetch key
 */

export function fetchWrappingKey (method = 'get') {
  return cache(
    `wrappingKey${method}`,
    () =>
      fetch(
        method === 'put' ? URL_GENERATE_WRAPPING_KEY : URL_WRAPPING_CRYPTO_KEY,
        { method, credentials: 'same-origin', headers: { 'Cookie': self.cookie } }
      )
        .then(resp => resp.json())
        .then(findProps(['privateJWK', 'publicJWK', 'privatePEM', 'publicPEM']))
        .then(
          ({
            privateJWK,
            publicJWK,
            privatePEM,
            publicPEM
          }) =>
            privatePEM && publicPEM
            ? importPEM({ privatePEM, publicPEM })
            : importJWK({ privateJWK, publicJWK })
        )
  )
}

/**
 * JWK keys
 * import
 */

export function importJWK ({ publicJWK, privateJWK }) {
  publicJWK = typeof publicJWK === 'string' ? JSON.parse(publicJWK) : publicJWK
  privateJWK = typeof privateJWK === 'string' ? JSON.parse(privateJWK) : privateJWK
  return {
    privateKey: importPrivateJWK(privateJWK),
    publicKey: importPublicJWK(publicJWK)
  }
}

function importPrivateJWK ({ alg, d, dp, dq, e, kid, kty, n, p, q, qi, use }) {
  if (kty !== 'RSA') {
    throw new Error(
      'Unsupported key algorithm "' + kty + '"; algorithm must be "RSA".'
    )
  }
  return forge.pki.setRsaPrivateKey(
    base64BigInt(n),
    base64BigInt(e),
    base64BigInt(d),
    base64BigInt(p),
    base64BigInt(q),
    base64BigInt(dp),
    base64BigInt(dq),
    base64BigInt(qi)
  )
}

function importPublicJWK ({ alg, e, kid, kty, n, use }) {
  if (kty !== 'RSA') {
    throw new Error('Key algorithm must be "RSA".')
  }
  return forge.pki.setRsaPublicKey(
    base64BigInt(n),
    base64BigInt(e)
  )
}

/**
 * JWK keys
 * export
 */

export function exportJWK ({ publicKey, privateKey }) {
  return {
    publicJWK: exportPublicJWK(publicKey),
    privateJWK: exportPrivateJWK(privateKey)
  }
}

/**
 * AES key wrap/unwrap
 */

const RSA_ALG = 'RSA-OAEP'
const RSA_OPTIONS = {}

export function wrapKey (key) {
  return fetchWrappingKey('put')
    .then(
      ({ publicKey, privateKey }) => {
        const encryptedKey = publicKey.encrypt(key, RSA_ALG, RSA_OPTIONS)
        if (key !== privateKey.decrypt(encryptedKey, RSA_ALG, RSA_OPTIONS)) {
          throw new Error('Key wrapping failure. Check RSA keys')
        }
        return encryptedKey
      })
}

export function unwrapKey (key) {
  return fetchWrappingKey()
    .then(({ privateKey }) => privateKey.decrypt(key, RSA_ALG, RSA_OPTIONS))
    .catch(e => debug.warn('Failed to unwrap key\n', e) || null)
}

function exportPrivateJWK ({ d, dP, dQ, e, n, p, q, qInv }) {
  return {
    kty: 'RSA',
    d: bigintBase64(d),
    dp: bigintBase64(dP),
    dq: bigintBase64(dQ),
    e: bigintBase64(e),
    n: bigintBase64(n),
    p: bigintBase64(p),
    q: bigintBase64(q),
    qi: bigintBase64(qInv)
  }
}

function exportPublicJWK ({ n, e }) {
  return {
    kty: 'RSA',
    n: bigintBase64(n),
    e: bigintBase64(e)
  }
}

/**
 * key testing api
 */

export const testKeys = (keys, alg, keyFormat) => {
  const bytes = 'capybara'

  const { publicKey, privateKey } = keyFormat === 'pem' ? importPEM(keys) : importJWK(keys)
  console.log('Testing keys:', publicKey, privateKey)
  const encrypted = publicKey.encrypt(bytes, alg, RSA_OPTIONS)
  console.log('Encrypted:', encrypted)
  const decrypted = privateKey.decrypt(encrypted, alg, RSA_OPTIONS)
  console.log('Decrypted:', decrypted)

  return decrypted === bytes
}
